import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CanActivateFn } from '@angular/router';
import { UserService } from '../services/user.service';
import { AuthService } from '../services/auth.service';
import { Profile } from '../models/user.model';

async function resolveSession(
  userService: UserService,
  authService: AuthService,
): Promise<boolean> {
  if (userService.isAuthenticated()) return true;

  const firebaseUser = await authService.isLoggedIn();
  if (!firebaseUser || firebaseUser.isAnonymous) return false;

  if (!userService.isAuthenticated()) {
    await userService.loadUserFromAuth(firebaseUser.uid, firebaseUser.email ?? '');
  }
  return true;
}

export const authGuard: CanActivateFn = async (route, state) => {
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const authenticated = await resolveSession(userService, authService);
  if (authenticated) return true;

  console.warn('Acceso denegado: Usuario no autenticado');
  router.navigate(['/session/login']);
  return false;
};

export const hrAdminGuard: CanActivateFn = async (route, state) => {
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const authenticated = await resolveSession(userService, authService);
  if (!authenticated) {
    console.warn('Acceso denegado: Usuario no autenticado');
    router.navigate(['/session/login']);
    return false;
  }

  if (userService.isHrAdmin()) {
    console.log('Acceso permitido: Usuario es HR Admin');
    return true;
  }

  console.warn('Acceso denegado: Se requiere rol de HR Admin');
  router.navigate(['/home']);
  return false;
};

export const roleGuard = (allowedRoles: Profile['rol'][]): CanActivateFn => {
  return async (route, state) => {
    const userService = inject(UserService);
    const authService = inject(AuthService);
    const router = inject(Router);

    const authenticated = await resolveSession(userService, authService);
    if (!authenticated) {
      console.warn('Acceso denegado: Usuario no autenticado');
      router.navigate(['/session/login']);
      return false;
    }

    const profile = userService.getCurrentProfile();
    const userRole = profile?.rol;

    if (userRole && allowedRoles.includes(userRole)) {
      console.log(`Acceso permitido: Usuario tiene rol "${userRole}"`);
      return true;
    }

    console.warn(
      `Acceso denegado: Se requiere uno de estos roles: [${allowedRoles.join(', ')}]. Usuario tiene: "${userRole}"`,
    );
    router.navigate(['/home']);
    return false;
  };
};

export const guestGuard: CanActivateFn = async (route, state) => {
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const authenticated = await resolveSession(userService, authService);

  if (authenticated) {
    router.navigate(['/home']);
    return false;
  }

  return true;
};
